/**
 * The device's own position, for "take me to where I am".
 *
 * A phone answers from GPS; a desktop browser usually from Wi-Fi or its IP
 * address, which can be kilometres out — so the accuracy travels with the fix
 * and is shown to the player rather than hidden.
 *
 * Only works in a secure context (https, or localhost in development), and
 * only after the player grants permission; every way it can fail comes back as
 * a sentence fit to show them.
 */

export interface Fix {
  lat: number;
  lon: number;
  /** Radius of the 68 % confidence circle, metres. */
  accuracy: number;
}

export function locate(): Promise<Fix> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator) || !window.isSecureContext) {
      reject(new Error('Location is not available in this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }),
      (e) => reject(new Error(
        e.code === e.PERMISSION_DENIED
          ? 'Location permission was denied — allow it in the browser’s site settings.'
          : e.code === e.TIMEOUT
            ? 'Finding your location took too long — try again.'
            : 'Your location could not be found.',
      )),
      // High accuracy wakes the GPS on a phone; a desktop without one just
      // answers from its network position either way. A fix a minute old is
      // still where the player is.
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  });
}

/** "±12 m", "±3 km" — how far off the fix may be. */
export function accuracyText(metres: number): string {
  return metres < 1000 ? `±${Math.round(metres)} m` : `±${(metres / 1000).toFixed(metres < 10_000 ? 1 : 0)} km`;
}
